import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { RouteComponentProps, useLocation } from 'react-router-dom';
import moment from 'moment-timezone';

import FilterBar, { FilterField, FilterValues } from '../../../components/Filter';
import EstateUnitFilter from '../../../components/EstateUnitFilter';
import PopupModal from '../../../components/PopupModal';
import { CustoDropDownItemProps } from '../../../components/types';
import { GetBondInsuranceProposalsListResponseDTO } from '../../../dtos/bond-insurance-proposal-list/get-bond-insurance-proposals-list-response-dto';
import { ProposalForGetBondInsuranceProposalsListResponseDTO } from '../../../dtos/bond-insurance-proposal-list/interfaces/proposal-for-get-bond-insurance-proposals-list-response-dto';
import { sortBondInsuranceProposals } from '../../../services/bond-insurance-proposal';
import {
  getBrokerBondInsuranceProposals,
  useDropDownUsers
} from '../../../services/broker';
import { dangerNotification } from '../../../services/notification';
import { iRootState } from '../../../store';
import { AvailableBrokersAndEstatesState } from '../../../store/types/temp-types';
import { BannerLocationActivationEnum } from '../../../types/Banner';
import { InsurerState } from '../../../types/InsurerState';
import { UsersToCustomDropDownOptions } from '../../../util';
import { appendEstateUnitIdToFilter } from '../../../utils/estateUnitFilter.util';
import { isBackOfficeUser } from '../../../utils/isBackOfficeUser';
import { isEstateRole } from '../../../utils/isEstateRole';
import { typeOptions } from '../../find-record/types/options-find.type';
import { ProposalsFilterData, ProposalsListingRouteInfo } from '../types';
import BondInsuranceProposalsTable from './BondInsuranceProposalsTable';

export type BondInsuranceProposalsListProps = {
  availableBrokersAndEstates: iRootState['availableBrokersAndEstates'];
  user: iRootState['user'];
  updateAvailableBrokersAndEstates: (
    availableBrokers: AvailableBrokersAndEstatesState
  ) => AvailableBrokersAndEstatesState | void;
} & RouteComponentProps<ProposalsListingRouteInfo>;

interface LocationState {
  bondInsuranceId?: string;
}

type SearchField =
  | 'document'
  | 'property_document'
  | 'name'
  | 'property_name'
  | 'address';

type ApiFilters = Partial<ProposalsFilterData> & {
  estateUnitId?: string;
  property_document?: string;
  property_name?: string;
};

const SEARCH_FIELDS: SearchField[] = [
  'document',
  'property_document',
  'name',
  'property_name',
  'address'
];

const PROPOSAL_SEARCH_TYPES = typeOptions.slice(0, -2);

const STATUS_UI_TO_API: Record<string, number> = {
  'Contratação em Andamento': 1,
  'Erro na Contratação': 2,
  'Contratação integrada em Andamento': 5
};

const hiredDataStatusOptions = [
  {
    value: 'Contratação em Andamento',
    text: 'Contratação em Andamento'
  },
  {
    value: 'Erro na Contratação',
    text: 'Erro na Contratação'
  },
  {
    value: 'Contratação integrada em Andamento',
    text: 'Contratação integrada em Andamento'
  }
];

const toSelectOptions = (
  items: Array<{ value: any; text: string }>
): Array<{ value: string; label: string }> =>
  items.map(item => ({
    value: String(item.value),
    label: String(item.text)
  }));

const formatDateValue = (value: any): string | undefined => {
  if (!value) return undefined;
  if (value instanceof Date) {
    return moment(value).format('YYYY-MM-DD');
  }
  const asString = String(value);
  if (/^\d{4}-\d{2}-\d{2}/.test(asString)) {
    return asString.substring(0, 10);
  }
  const parsed = moment(asString, ['YYYY-MM-DD', 'DD/MM/YYYY'], true);
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : asString;
};

const buildInitialFilterValues = (singleEstateId: string): FilterValues => ({
  type: 'document',
  argument: '',
  insurer: '',
  estate_id: singleEstateId,
  start: '',
  end: '',
  status: '',
  estateUnitId: '',
  accountManager: ''
});

const normalizeToApiFilter = (values: FilterValues): ApiFilters => {
  const searchType = values.type as SearchField | undefined;
  const argument = String(values.argument || '').trim();

  const filter: ApiFilters = {};

  SEARCH_FIELDS.forEach(field => {
    filter[field] = '';
  });

  if (searchType && argument && SEARCH_FIELDS.includes(searchType)) {
    filter[searchType] = argument;
  }

  if (values.insurer) filter.insurer = String(values.insurer);
  if (values.estate_id) filter.estate_id = String(values.estate_id);

  const start = formatDateValue(values.start);
  const end = formatDateValue(values.end);
  if (start) filter.start = start;
  if (end) filter.end = end;

  const statusLabel = String(values.status || '');
  if (statusLabel && statusLabel !== 'Todos' && STATUS_UI_TO_API[statusLabel] != null) {
    filter.status = STATUS_UI_TO_API[statusLabel];
  }

  if (values.accountManager) {
    filter.accountManager = String(values.accountManager);
  }

  return appendEstateUnitIdToFilter(
    filter as Record<string, unknown>,
    String(values.estateUnitId || '')
  ) as ApiFilters;
};

const BondInsuranceProposalsPage = ({
  availableBrokersAndEstates,
  user
}: BondInsuranceProposalsListProps) => {
  const location = useLocation();
  const locationState = location.state as LocationState | undefined;

  const estates: CustoDropDownItemProps[] = availableBrokersAndEstates.estates;
  const insurers: InsurerState[] = availableBrokersAndEstates.bondInsurers.map(
    (insurer: any) => insurer.value
  );

  const isBackOffice = isBackOfficeUser();
  const isEstateUser = isEstateRole(user?.role);

  const singleEstateId = useMemo(() => {
    if (estates.length !== 1) return '';
    const value = estates[0]?.value;
    return String(typeof value === 'object' && value?.id ? value.id : value || '');
  }, [estates]);

  const initialFilterValues = useMemo(
    () => buildInitialFilterValues(singleEstateId),
    [singleEstateId]
  );

  const hasDeepLink = Boolean(locationState?.bondInsuranceId);

  const [bondInsuranceProposals, setBondInsuranceProposals] = useState<
    ProposalForGetBondInsuranceProposalsListResponseDTO[]
  >([]);
  const [isLoading, setIsLoading] = useState(false);
  const [ascendingOrder] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [dataCount, setDataCount] = useState(0);
  const [bondInsuranceId, setBondInsuranceId] = useState<string | undefined>(
    locationState?.bondInsuranceId
  );
  const [appliedFilters, setAppliedFilters] = useState<ApiFilters>(() =>
    normalizeToApiFilter(initialFilterValues)
  );
  const [isEnabledToFetch, setIsEnabledToFetch] = useState(
    hasDeepLink || !isBackOffice
  );

  const appliedFiltersRef = useRef(appliedFilters);
  const bondInsuranceIdRef = useRef(bondInsuranceId);

  useEffect(() => {
    appliedFiltersRef.current = appliedFilters;
  }, [appliedFilters]);

  useEffect(() => {
    bondInsuranceIdRef.current = bondInsuranceId;
  }, [bondInsuranceId]);

  const selectableInsurers = useMemo(
    () =>
      insurers.map(insurer => ({
        value: insurer.id,
        text: insurer.name
      })),
    [insurers]
  );

  const estateOptions = useMemo(
    () =>
      estates.map(item => {
        const valueId =
          typeof item.value === 'object' && item.value?.id
            ? item.value.id
            : item.value;
        return {
          value: String(valueId || ''),
          text: String(item.text || '')
        };
      }),
    [estates]
  );

  const {
    data: usersData,
    isLoading: isAccountManagerLoading,
    isError: isAccountManagerFetchError
  } = useDropDownUsers(user?.broker?.id);

  useEffect(() => {
    if (isAccountManagerFetchError) {
      dangerNotification(
        'Oops...',
        'Não foi possível encontrar os analistas disponíveis'
      );
    }
  }, [isAccountManagerFetchError]);

  const accountManagerOptions = useMemo(() => {
    if (!usersData) return [];

    return UsersToCustomDropDownOptions(
      false,
      usersData.map((item: any) => ({
        id: item.id,
        name: item.name
      }))
    ).map((item: any) => ({
      value: String(item.value),
      text: String(item.text)
    }));
  }, [usersData]);

  const filterFields: FilterField[] = useMemo(() => {
    const fields: FilterField[] = [
      {
        kind: 'search',
        name: 'search',
        label: 'Pesquisar',
        placeholder: 'Digite...',
        defaultType: 'document',
        types: PROPOSAL_SEARCH_TYPES.map(option => ({
          value: String(option.value),
          label: String(option.text)
        })),
        width: 1.6
      },
      {
        kind: 'select',
        name: 'insurer',
        label: 'Seguradora',
        placeholder: 'Selecionar...',
        options: toSelectOptions(selectableInsurers),
        getDisplayValue: values => {
          const option = selectableInsurers.find(
            item => String(item.value) === String(values.insurer)
          );
          return option ? option.text : undefined;
        }
      }
    ];

    if (!isEstateUser) {
      fields.push({
        kind: 'select',
        name: 'estate_id',
        label: 'Imobiliária',
        placeholder: 'Selecionar...',
        options: toSelectOptions(estateOptions),
        getDisplayValue: values => {
          const option = estateOptions.find(
            item => String(item.value) === String(values.estate_id)
          );
          return option ? option.text : undefined;
        }
      });
    }

    fields.push(
      {
        kind: 'dateRange',
        name: 'period',
        label: 'Período',
        startName: 'start',
        endName: 'end'
      },
      {
        kind: 'select',
        name: 'status',
        label: 'Status',
        placeholder: 'Selecionar...',
        options: toSelectOptions(hiredDataStatusOptions),
        getDisplayValue: values => {
          const option = hiredDataStatusOptions.find(
            item => String(item.value) === String(values.status)
          );
          return option ? option.text : undefined;
        }
      },
      {
        kind: 'custom',
        name: 'estateUnitId',
        label: 'Unidade',
        advanced: true,
        getDisplayValue: values => {
          if (!values.estateUnitId) return undefined;
          if (values.estateUnitId === 'null') return 'Sem unidade';
          return String(values.estateUnitId);
        },
        render: ({ values, setValue }) => (
          <EstateUnitFilter
            estateId={String(values.estate_id || user?.estate?.id || '')}
            value={values.estateUnitId || ''}
            onChange={value => setValue('estateUnitId', value)}
            upward
          />
        )
      }
    );

    if (!isEstateUser) {
      fields.push({
        kind: 'select',
        name: 'accountManager',
        label: 'Gestor de Contas',
        placeholder: 'Selecionar...',
        advanced: true,
        loading: isAccountManagerLoading,
        options: toSelectOptions(accountManagerOptions),
        getDisplayValue: values => {
          const option = accountManagerOptions.find(
            item => String(item.value) === String(values.accountManager)
          );
          return option ? option.text : undefined;
        }
      });
    }

    return fields;
  }, [
    isEstateUser,
    selectableInsurers,
    estateOptions,
    accountManagerOptions,
    isAccountManagerLoading,
    user?.estate?.id
  ]);

  const loadTotal = useCallback(
    async (filter: ApiFilters) => {
      const data: GetBondInsuranceProposalsListResponseDTO =
        await getBrokerBondInsuranceProposals(currentPage, {
          ...filter,
          action: 'COUNT'
        });
      setDataCount(data.count);
    },
    [currentPage]
  );

  const getData = useCallback(async () => {
    setIsLoading(true);

    try {
      const filter: ApiFilters = {
        ...appliedFiltersRef.current,
        ...(bondInsuranceIdRef.current
          ? { bondInsuranceId: bondInsuranceIdRef.current }
          : {})
      };

      if (!filter.estate_id && singleEstateId) {
        filter.estate_id = singleEstateId;
      }

      delete filter.broker_id;

      const data: GetBondInsuranceProposalsListResponseDTO =
        await getBrokerBondInsuranceProposals(currentPage, filter);

      const orderedProposals: ProposalForGetBondInsuranceProposalsListResponseDTO[] =
        data.proposals.length > 1
          ? sortBondInsuranceProposals(data.proposals, ascendingOrder)
          : data.proposals;

      if (currentPage === 1) {
        await loadTotal(filter);
      }

      setBondInsuranceProposals(orderedProposals);

      if (bondInsuranceIdRef.current) {
        setBondInsuranceId(undefined);
      }
    } catch (e) {
      dangerNotification('Oops...', 'Não foi possível buscar as propostas');
    } finally {
      setIsLoading(false);
    }
  }, [ascendingOrder, currentPage, loadTotal, singleEstateId]);

  useEffect(() => {
    window.history.replaceState(
      {
        ...(location.state as any),
        bondInsuranceId: undefined
      },
      ''
    );
  }, [location]);

  useEffect(() => {
    if (!isBackOffice) {
      setIsEnabledToFetch(true);
    }
  }, [isBackOffice]);

  useEffect(() => {
    if (!isEnabledToFetch) return;
    getData();
  }, [currentPage, appliedFilters, isEnabledToFetch, getData]);

  const handleFilterSearch = (values: FilterValues) => {
    const next = normalizeToApiFilter(values);
    if (!next.estate_id && singleEstateId) {
      next.estate_id = singleEstateId;
    }
    setBondInsuranceId(undefined);
    setAppliedFilters(next);
    setCurrentPage(1);
    setIsEnabledToFetch(true);
  };

  const handleFilterClear = () => {
    setBondInsuranceId(undefined);
    setCurrentPage(1);
    setIsEnabledToFetch(true);
  };

  return (
    <>
      <FilterBar
        fields={filterFields}
        initialValues={initialFilterValues}
        onSearch={handleFilterSearch}
        onClear={handleFilterClear}
        loading={isLoading}
        syncToUrl
        urlNamespace="proposals"
        hasExternalActiveFilters={Boolean(bondInsuranceId)}
      />

      <BondInsuranceProposalsTable
        bondInsuranceProposals={bondInsuranceProposals}
        setIsLoading={setIsLoading}
        isLoading={isLoading}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        getData={getData}
        dataCount={dataCount}
      />

      <PopupModal
        locationActivation={BannerLocationActivationEnum.BOND_INSURANCE_PROPOSAL_LIST}
      />
    </>
  );
};

export default BondInsuranceProposalsPage;
